var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useEffect, useRef, useReducer, useState } from 'react';
import { parseClassName, isFunction, isNumber, getAutoCloseDelay, toToastItem, } from '../utils';
import { eventManager } from '../core/eventManager';
import { EventActions } from '../utils';
import { getIcon } from '../components/Icons';
export function useToastContainer(props) {
    var _a = useReducer(function (x) { return x + 1; }, 0), forceUpdate = _a[1];
    var _b = useState([]), toastIds = _b[0], setToastIds = _b[1];
    var containerRef = useRef(null);
    var toastToRender = useRef(new Map()).current;
    var isToastActive = function (id) { return toastIds.indexOf(id) !== -1; };
    var instance = useRef({
        toastKey: 1,
        displayedToast: 0,
        count: 0,
        queue: [],
        props: props,
        isToastActive: isToastActive,
        getToast: function (id) { return toastToRender.get(id); },
    }).current;
    useEffect(function () {
        eventManager
            .cancelEmit(EventActions.WillUnmount)
            .on(EventActions.Show, buildToast)
            .on(EventActions.Clear, function (toastId) { return containerRef.current && removeToast(toastId); })
            .emit(EventActions.DidMount, instance);
        return function () {
            toastToRender.clear();
            eventManager.emit(EventActions.WillUnmount, instance);
        };
    }, []);
    useEffect(function () {
        instance.props = props;
        instance.isToastActive = isToastActive;
        instance.displayedToast = toastIds.length;
    });
    function removeToast(toastId) {
        setToastIds(function (state) {
            return toastId == null ? [] : state.filter(function (id) { return id !== toastId; });
        });
    }
    function dequeueToast() {
        var _a = instance.queue.shift(), toastProps = _a.toastProps, staleId = _a.staleId;
        appendToast(toastProps, staleId);
    }
    /**
     * check if a container is attached to the dom
     * check for multi-container, build only if associated
     * check for duplicate toastId if no update
     */
    function isNotValid(options) {
        return toastToRender.has(options.toastId) && options.updateId == null;
    }
    // this function and all the function called inside needs to rely on refs
    function buildToast(_a) {
        var delay = _a.delay, staleId = _a.staleId, options = __rest(_a, ["delay", "staleId"]);
        if (isNotValid(options))
            return;
        var toastId = options.toastId, updateId = options.updateId;
        var props = instance.props;
        var closeToast = function () { return removeToast(toastId); };
        var isNotAnUpdate = updateId == null;
        if (isNotAnUpdate)
            instance.count++;
        var toastProps = __assign(__assign(__assign(__assign({}, props), { style: props.toastStyle, key: instance.toastKey++, hasCloseButton: true }), options), { toastId: toastId, updateId: updateId, closeToast: closeToast, isIn: false, className: parseClassName(options.className || props.toastClassName), bodyClassName: parseClassName(options.bodyClassName || props.bodyClassName), progressClassName: parseClassName(options.progressClassName || props.progressClassName), autoClose: getAutoCloseDelay(options.autoClose, props.autoClose), deleteToast: function () {
                var removed = toToastItem(toastToRender.get(toastId), 'removed');
                toastToRender.delete(toastId);
                eventManager.emit(EventActions.Change, removed);
                var queueLen = instance.queue.length;
                instance.count =
                    toastId == null
                        ? instance.count - instance.displayedToast
                        : instance.count - 1;
                if (instance.count < 0)
                    instance.count = 0;
                if (queueLen > 0) {
                    var freeSlot = toastId == null ? instance.props.limit : 1;
                    if (queueLen === 1 || freeSlot === 1) {
                        instance.displayedToast++;
                        dequeueToast();
                    }
                    else {
                        var toDequeue = freeSlot > queueLen ? queueLen : freeSlot;
                        instance.displayedToast = toDequeue;
                        for (var i = 0; i < toDequeue; i++)
                            dequeueToast();
                    }
                }
                else {
                    forceUpdate();
                }
            } });
        toastProps.icon = getIcon(toastProps);
        if (isFunction(options.onOpen))
            toastProps.onOpen = options.onOpen;
        if (isFunction(options.onClose))
            toastProps.onClose = options.onClose;
        // not handling limit + delay by design. Waiting for user feedback first
        if (props.limit &&
            props.limit > 0 &&
            instance.count > props.limit &&
            isNotAnUpdate) {
            instance.queue.push({ toastProps: toastProps, staleId: staleId });
        }
        else if (isNumber(delay)) {
            setTimeout(function () {
                appendToast(toastProps, staleId);
            }, delay);
        }
        else {
            appendToast(toastProps, staleId);
        }
    }
    function appendToast(toastProps, staleId) {
        var toastId = toastProps.toastId;
        if (staleId)
            toastToRender.delete(staleId);
        var toast = {
            props: toastProps,
        };
        toastToRender.set(toastId, toast);
        setToastIds(function (state) { return __spreadArray(__spreadArray([], state, true), [toastId], false).filter(function (id) { return id !== staleId; }); });
        eventManager.emit(EventActions.Change, toToastItem(toast, toast.props.updateId == null ? 'added' : 'updated'));
    }
    function getToastToRender(cb) {
        var toRender = new Map();
        var collection = Array.from(toastToRender.values());
        if (props.newestOnTop)
            collection.reverse();
        collection.forEach(function (toast) {
            var position = toast.props.position;
            toRender.has(position) || toRender.set(position, []);
            toRender.get(position).push(toast);
        });
        return Array.from(toRender, function (p) { return cb(p[0], p[1]); });
    }
    return {
        getToastToRender: getToastToRender,
        containerRef: containerRef,
        isToastActive: isToastActive,
    };
}
